// Interface for encrypted payloads
export interface EncryptedPayload {
  iv: string;
  encryptedData: string;
  encrypted: boolean;
}

/**
 * Utility function to convert a hex string to Uint8Array
 */
export function hexStringToUint8Array(hexString: string): Uint8Array {
  const match = hexString.match(/.{1,2}/g);
  if (!match) {
    throw new Error('Invalid hex string');
  }
  return new Uint8Array(match.map((byte) => parseInt(byte, 16)));
}

/**
 * Utility function to convert Uint8Array to hex string
 */
export function uint8ArrayToHexString(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Utility function to convert base64 to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Utility function to convert Uint8Array to base64
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Generate a cryptographically secure random IV
 */
async function generateIV(): Promise<Uint8Array> {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Encrypt payload using AES-GCM with Web Crypto API
 * @param data Object to encrypt
 * @param encryptionKey Hex string key for encryption
 * @returns Encrypted payload with IV and authentication tag (auth tag is included at the end of encryptedData)
 */
export async function encryptPayload(
  data: any,
  encryptionKey: string
): Promise<EncryptedPayload> {
  try {
    // Convert the key from hex string to Uint8Array
    const keyData = hexStringToUint8Array(encryptionKey);

    // Generate a random initialization vector
    const iv = await generateIV();

    // Create a CryptoKey from the raw key data
    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt']
    );

    // Prepare the data
    const jsonData = JSON.stringify(data);
    const encodedData = new TextEncoder().encode(jsonData);

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
        tagLength: 128, // 16 bytes
      },
      key,
      encodedData
    );

    // Convert to base64 strings
    const ivBase64 = uint8ArrayToBase64(iv);
    const encryptedBase64 = uint8ArrayToBase64(new Uint8Array(encryptedBuffer));

    // Return the encrypted data with iv
    return {
      iv: ivBase64,
      encryptedData: encryptedBase64,
      encrypted: true,
    };
  } catch (error) {
    console.error('Encryption failed:', error);
    // If encryption fails, return the original data unencrypted
    return {
      iv: '',
      encryptedData: JSON.stringify(data),
      encrypted: false,
    };
  }
}
